import { supabase } from '../lib/supabase';

// Service Outages
export const outageService = {
  // Get all outages with provider information
  async getAllOutages(filters = {}) {
    try {
      let query = supabase?.from('service_outages')?.select(`
          *,
          provider:service_providers(*),
          reporter:user_profiles(id, full_name)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.severity) {
        query = query?.eq('severity', filters?.severity);
      }
      if (filters?.category) {
        query = query?.eq('service_providers.category', filters?.category);
      }
      if (filters?.location_city) {
        query = query?.eq('location_city', filters?.location_city);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error('Error fetching outages:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get trending outages (high report counts, recent)
  async getTrendingOutages(limit = 10) {
    try {
      const { data, error } = await supabase?.from('service_outages')?.select(`
          *,
          provider:service_providers(*),
          reporter:user_profiles(id, full_name)
        `)?.gte('report_count', 5)?.gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString())?.order('report_count', { ascending: false })?.order('upvote_count', { ascending: false })?.limit(limit);

      return { data, error };
    } catch (error) {
      console.error('Error fetching trending outages:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get outage by ID with full details
  async getOutageById(id) {
    try {
      const { data, error } = await supabase?.from('service_outages')?.select(`
          *,
          provider:service_providers(*),
          reporter:user_profiles(id, full_name, location_city, location_country),
          comments:outage_comments(
            *,
            user:user_profiles(id, full_name)
          )
        `)?.eq('id', id)?.single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching outage by ID:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Create new outage report
  async createOutage(outageData) {
    try {
      const user = await supabase?.auth?.getUser();
      if (!user?.data?.user) {
        return { data: null, error: { message: 'Authentication required' } };
      }

      const { data, error } = await supabase?.from('service_outages')?.insert([{
          ...outageData,
          reporter_id: user?.data?.user?.id
        }])?.select(`
          *,
          provider:service_providers(*)
        `)?.single();

      return { data, error };
    } catch (error) {
      console.error('Error creating outage:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Update outage status (for admins/moderators)
  async updateOutageStatus(id, status, resolution_notes = null) {
    try {
      const updateData = { 
        status, 
        updated_at: new Date()?.toISOString()
      };
      
      if (status === 'resolved') {
        updateData.resolved_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('service_outages')?.update(updateData)?.eq('id', id)?.select()?.single();

      return { data, error };
    } catch (error) {
      console.error('Error updating outage status:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Vote on outage (upvote/downvote)
  async voteOnOutage(outageId, voteType) {
    try {
      const user = await supabase?.auth?.getUser();
      if (!user?.data?.user) {
        return { data: null, error: { message: 'Authentication required' } };
      }

      // First, check if user already voted
      const { data: existingVote } = await supabase?.from('outage_votes')?.select('*')?.eq('user_id', user?.data?.user?.id)?.eq('outage_id', outageId)?.single();

      if (existingVote) {
        // Update existing vote
        const { data, error } = await supabase?.from('outage_votes')?.update({ vote_type: voteType })?.eq('id', existingVote?.id)?.select()?.single();
        
        return { data, error };
      } else {
        // Create new vote
        const { data, error } = await supabase?.from('outage_votes')?.insert([{
            user_id: user?.data?.user?.id,
            outage_id: outageId,
            vote_type: voteType
          }])?.select()?.single();

        return { data, error };
      }
    } catch (error) {
      console.error('Error voting on outage:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Subscribe to outage notifications for a provider
  async subscribeToProvider(providerId) {
    try {
      const user = await supabase?.auth?.getUser();
      if (!user?.data?.user) {
        return { data: null, error: { message: 'Authentication required' } };
      }

      const { data, error } = await supabase?.from('outage_subscriptions')?.upsert([{
          user_id: user?.data?.user?.id,
          provider_id: providerId,
          notification_enabled: true
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      console.error('Error subscribing to provider:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  }
};

// Service Providers
export const providerService = {
  // Get all providers
  async getAllProviders(category = null) {
    try {
      let query = supabase?.from('service_providers')?.select('*')?.eq('status', 'active')?.order('name');

      if (category) {
        query = query?.eq('category', category);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error('Error fetching providers:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Get provider by ID with recent outages
  async getProviderById(id) {
    try {
      const { data, error } = await supabase?.from('service_providers')?.select(`
          *,
          outages:service_outages(
            *,
            reporter:user_profiles(id, full_name)
          )
        `)?.eq('id', id)?.single();

      return { data, error };
    } catch (error) {
      console.error('Error fetching provider by ID:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  }
};

// Comments
export const commentService = {
  // Get comments for an outage
  async getCommentsForOutage(outageId) {
    try {
      const { data, error } = await supabase?.from('outage_comments')?.select(`
          *,
          user:user_profiles(id, full_name),
          replies:outage_comments(
            *,
            user:user_profiles(id, full_name)
          )
        `)?.eq('outage_id', outageId)?.is('parent_comment_id', null)?.order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      console.error('Error fetching comments:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Add comment to outage
  async addComment(outageId, content, parentCommentId = null) {
    try {
      const user = await supabase?.auth?.getUser();
      if (!user?.data?.user) {
        return { data: null, error: { message: 'Authentication required' } };
      }

      const { data, error } = await supabase?.from('outage_comments')?.insert([{
          outage_id: outageId,
          user_id: user?.data?.user?.id,
          content,
          parent_comment_id: parentCommentId
        }])?.select(`
          *,
          user:user_profiles(id, full_name)
        `)?.single();

      return { data, error };
    } catch (error) {
      console.error('Error adding comment:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Update comment
  async updateComment(commentId, content) {
    try {
      const { data, error } = await supabase?.from('outage_comments')?.update({ 
          content,
          updated_at: new Date()?.toISOString()
        })?.eq('id', commentId)?.select(`
          *,
          user:user_profiles(id, full_name)
        `)?.single();

      return { data, error };
    } catch (error) {
      console.error('Error updating comment:', error);
      return { data: null, error: { message: 'Network error. Please try again.' } };
    }
  },

  // Delete comment
  async deleteComment(commentId) {
    try {
      const { error } = await supabase?.from('outage_comments')?.delete()?.eq('id', commentId);

      return { error };
    } catch (error) {
      console.error('Error deleting comment:', error);
      return { error: { message: 'Network error. Please try again.' } };
    }
  }
};

// Real-time subscriptions
export const realtimeService = {
  // Subscribe to outage updates
  subscribeToOutages(callback) {
    const channel = supabase?.channel('outages')?.on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_outages' },
        callback
      )?.subscribe();

    return channel;
  },

  // Subscribe to comments for a specific outage
  subscribeToOutageComments(outageId, callback) {
    const channel = supabase?.channel(`outage_comments_${outageId}`)?.on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'outage_comments',
          filter: `outage_id=eq.${outageId}`
        },
        callback
      )?.subscribe();

    return channel;
  },

  // Unsubscribe from channel
  unsubscribe(channel) {
    return supabase?.removeChannel(channel);
  }
};